import React, { useRef, useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Upload, CheckCircle2, Image as ImageIcon, Pencil, Trash2 } from "lucide-react";

// Helper: Truncate text for table cells
const truncate = (str, max = 60) => str?.length > max ? str.slice(0, max) + "..." : str;

const NEWS_API_URL = 'https://portal.lsb.go.ke/lsb/news/';

const initialFormState = {
    title: '',
    message: '',
    document: null,
    documentName: '',
    news_id: null,
    existingDocumentUrl: null,
    existingDocumentName: ''
};

const REFRESH_INTERVAL = 15000; // 15 seconds as per UI

const IMAGE_MIME_TYPES = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/gif",
    "image/webp",
    "image/bmp",
    "image/svg+xml"
];

// Helper: Is file an image?
function isImageFile(file) {
    return !!file && IMAGE_MIME_TYPES.includes(file.type);
}

// Helper: Is file name considered image by extension?
function isImageFilename(name = '') {
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
}

const NewResource = () => {
    const [form, setForm] = useState(initialFormState);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [addMode, setAddMode] = useState(false);
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [fetchError, setFetchError] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [fetchingEditDoc, setFetchingEditDoc] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(Date.now());
    const inputFileRef = useRef();

    // Fetch News
    const fetchNews = async () => {
        setLoading(true);
        setFetchError('');
        try {
            const accessToken = localStorage.getItem('accessToken');
            const res = await fetch(NEWS_API_URL, {
                method: 'GET',
                headers: {
                    'Authorization': accessToken ? `Bearer ${accessToken}` : '',
                }
            });
            if (!res.ok) throw new Error("Failed to fetch news");
            const data = await res.json();
            const rawNews = Array.isArray(data) ? data : (data?.results || []);
            // Normalize: Ensure every item has a news_id (fallback to id)
            const normalized = rawNews.map(n => ({
                ...n,
                news_id: n.news_id || n.id
            }));
            setNews(normalized);
            setLastRefresh(Date.now());
        } catch (err) {
            setFetchError(err.message || "Unable to fetch news.");
        } finally {
            setLoading(false);
        }
    };

    // Fetch News on mount and at an interval
    useEffect(() => {
        fetchNews();
        const intervalId = setInterval(() => {
            fetchNews();
        }, REFRESH_INTERVAL);
        return () => clearInterval(intervalId);
    }, []);

    // Helper: extract filename from URL
    function getFilenameFromUrl(url) {
        try {
            if (!url) return '';
            // Try to extract everything after last /
            const u = decodeURIComponent(url.split('?')[0]); // Remove query params
            return u.substring(u.lastIndexOf('/') + 1);
        } catch {
            return '';
        }
    }

    // Helper: Fetch and create File object from a URL, resolving as File or null on failure
    async function fetchFileFromUrl(url, filename) {
        try {
            const response = await fetch(url, { credentials: "include" });
            if (!response.ok) return null;
            const blob = await response.blob();

            // Guess the type by filename if blob type is generic
            let type = blob.type;
            if (!type || type === "application/octet-stream") {
                if (isImageFilename(filename)) {
                    const ext = filename.split(".").pop().toLowerCase();
                    switch (ext) {
                        case "png":
                            type = "image/png"; break;
                        case "jpg":
                        case "jpeg":
                            type = "image/jpeg"; break;
                        case "webp":
                            type = "image/webp"; break;
                        case "gif":
                            type = "image/gif"; break;
                        case "bmp":
                            type = "image/bmp"; break;
                        case "svg":
                            type = "image/svg+xml"; break;
                        default:
                            break;
                    }
                }
                // fallback: type remains as blob.type
            }
            // Defensive: fallback filename if none exists
            const name = filename || 'attachment.png';
            // Use image type detected or fallback to image/png
            return new File([blob], name, { type: type || 'image/png' });
        } catch (err) {
            return null;
        }
    }

    // File handler - the server expects 'document' for the upload
    const handleFileChange = e => {
        const file = e.target.files[0];
        if (file && isImageFile(file)) {
            setForm(f => ({
                ...f,
                document: file,
                documentName: file.name,
                // When uploading new file, remove reference to existing
                existingDocumentUrl: null,
                existingDocumentName: ''
            }));
        } else {
            setForm(f => ({ ...f, document: null, documentName: '' }));
        }
    };

    // Modal open for edit ONLY
    const openEditModal = async (n) => {
        setFetchingEditDoc(true);
        const url = n.document || null;
        const fname = n.document ? getFilenameFromUrl(n.document) : '';
        let fileObject = null;
        // Only fetch file if you plan to re-upload, not just for preview
        setForm({
            title: n.title || '',
            message: n.message || '',
            document: null, // don't fetch File object unless being replaced
            documentName: '',
            news_id: n.news_id || n.id,
            existingDocumentUrl: url,
            existingDocumentName: fname,
        });
        setEditMode(true);
        setAddMode(false);
        setShowModal(true);
        setFetchingEditDoc(false);
    };

    // Modal open for "add" mode
    const openAddModal = () => {
        setForm(initialFormState);
        setAddMode(true);
        setEditMode(false);
        setShowModal(true);
    };

    // Separate handler for adding new news (PUT)
    const handleAdd = async () => {
        setSubmitting(true);
        setSubmitError('');
        const formData = new FormData();
        formData.append('title', form.title);
        formData.append('message', form.message);
        // Only append attachment if present
        if (form.document) {
            formData.append('document', form.document);
        }
        const accessToken = localStorage.getItem('accessToken');

        try {
            const res = await fetch(NEWS_API_URL, {
                method: 'PUT',
                headers: {
                    'Authorization': accessToken ? `Bearer ${accessToken}` : '',
                },
                body: formData,
            });

            if (!res.ok) {
                let msg = 'Failed to add news.';
                try {
                    const data = await res.json();
                    if (data?.detail) msg = data.detail;
                } catch { }
                throw new Error(msg);
            }

            const resNews = await res.json();
            const normalized = { ...resNews, news_id: resNews.news_id || resNews.id };
            setNews(prev => [normalized, ...prev]);
            setShowModal(false);
            setForm(initialFormState);
            setEditMode(false);
            setAddMode(false);
            await Swal.fire({
                icon: 'success',
                title: 'News Added',
                text: 'The news has been added successfully.',
                timer: 2000,
                showConfirmButton: false,
            });
        } catch (err) {
            setSubmitError(err.message || "Error adding news.");
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: err.message || "Error adding news.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Handler for updating a specific news_id with user's edited/added content
    const handleAppendEdit = async () => {
        setSubmitting(true);
        setSubmitError('');

        let selectedNewsItem = news.find(
            n => (n.news_id === form.news_id || n.id === form.news_id)
        );

        if (!selectedNewsItem) {
            setSubmitError("Could not find selected news item to edit.");
            setSubmitting(false);
            return;
        }

        const updatedTitle = form.title;
        const updatedMessage = form.message;
        const updatedId = form.news_id || selectedNewsItem.news_id || selectedNewsItem.id;

        const formData = new FormData();

        formData.append('title', updatedTitle);
        formData.append('message', updatedMessage);
        formData.append('id', updatedId);

        // Only append document if a new one was chosen
        if (form.document) {
            formData.append('document', form.document);
        }

        const accessToken = localStorage.getItem('accessToken');
        // Proper PUT for updating: don't use "?id=XX/", just provide ID in DS/REST
        // But as legacy API uses query param, keep as is, but remove trailing slash for actual query param usage
        const requestUrl = `${NEWS_API_URL}?id=${encodeURIComponent(updatedId)}`;

        try {
            const res = await fetch(requestUrl, {
                method: 'PUT',
                headers: {
                    'Authorization': accessToken ? `Bearer ${accessToken}` : '',
                },
                body: formData,
            });

            if (!res.ok) throw new Error('Failed to update news.');

            const resNews = await res.json();
            const normalized = { ...resNews, news_id: resNews.news_id || resNews.id };

            setNews(prev =>
                prev.map(n =>
                    (n.news_id === updatedId || n.id === updatedId)
                        ? normalized
                        : n
                )
            );

            setShowModal(false);
            setForm(initialFormState);

            await Swal.fire({
                icon: 'success',
                title: 'News Updated',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (err) {
            await Swal.fire({
                icon: 'error',
                title: 'Update failed',
                text: err.message
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Unified Submit Handler
    const handleSubmit = e => {
        e.preventDefault();
        if (addMode) {
            handleAdd();
        } else if (editMode) {
            handleAppendEdit();
        }
    };

    // Delete News
    const handleDelete = async (id) => {
        const news_id = id;
        if (!news_id) {
            await Swal.fire({ icon: 'error', title: 'Error', text: 'Invalid News ID' });
            return;
        }

        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'Do you want to delete this news item?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });
        if (!result.isConfirmed) return;

        const accessToken = localStorage.getItem('accessToken');
        try {
            // Use news_id as query parameter for deletion
            const res = await fetch(`${NEWS_API_URL}?id=${encodeURIComponent(news_id)}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': accessToken ? `Bearer ${accessToken}` : '',
                },
            });
            if (!res.ok) {
                let msg = 'Failed to delete.';
                try {
                    const data = await res.json();
                    if (data?.detail) msg = data.detail;
                } catch { }
                throw new Error(msg);
            }
            setNews(prev => prev.filter(n => (n.news_id !== news_id && n.id !== news_id)));
            await Swal.fire({
                icon: 'success',
                title: 'Deleted!',
                text: 'The news has been deleted.',
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (err) {
            await Swal.fire({
                icon: 'error',
                title: 'Delete Failed',
                text: err.message || 'Failed to delete news.',
            });
        }
    };

    return (
        <div className="max-w-7xl mx-auto">
            <div className="pb-2 flex flex-col justify-end text-xs leading-2 font-medium">
                <span>Auto-refresh: every 15s</span>
                <span className="" title={new Date(lastRefresh).toLocaleString()}>
                    Last refreshed: {new Date(lastRefresh).toLocaleTimeString()}
                </span>
            </div>

            {/* Add News Modal Button */}
            <div className="flex justify-end items-center my-8">
                <button
                    onClick={openAddModal}
                    className="px-5 py-2 rounded-lg bg-[#3d0707] text-white font-medium hover:bg-[#551010] transition"
                >
                    + Add News
                </button>
            </div>

            {/* Modal */}
            {showModal && (editMode || addMode) && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm transition-all">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-0 overflow-hidden relative animate-in fade-in zoom-in duration-200">
                        <div className='bg-[#3d0707] p-6 text-white flex justify-between items-center'>
                            <h5 className="font-bold flex items-center gap-2">{addMode ? "Add News" : "Edit News"}</h5>

                            <button
                                className="text-white/80 hover:text-white transition-colors group"
                                onClick={() => {
                                    setShowModal(false);
                                    setEditMode(false);
                                    setAddMode(false);
                                    setForm(initialFormState);
                                }}
                                aria-label="Close"
                                style={{ transition: 'transform 0.3s, color 0.3s' }}
                            >
                                <i className="fa-solid fa-xmark text-2xl transition-transform duration-200 group-hover:scale-125 group-hover:rotate-90" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-2 px-8 py-2">
                            <div>
                                <label className="block mb-2 font-bold text-gray-700 text-[1vw] uppercase">Title</label>
                                <input
                                    type="text"
                                    value={form.title}
                                    onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-400 outline-none text-[1vw]"
                                    placeholder="Enter the news title"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block mb-2 font-bold text-gray-700 text-[1vw] uppercase">Message</label>
                                <textarea
                                    value={form.message}
                                    onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                                    rows={2}
                                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-400 outline-none text-[1vw]"
                                    placeholder="Write the news message"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block mb-2 font-bold text-gray-700 text-[1vw] uppercase">Attachment</label>
                                {/* Display current document if not replacing during edit, else handle normal upload UI */}
                                <div
                                    onClick={() => inputFileRef.current && inputFileRef.current.click()}
                                    className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-2 cursor-pointer transition
                                        ${(!!form.documentName || (!!form.existingDocumentUrl && !!form.existingDocumentName && editMode && !form.document))
                                            ? "border-emerald-500 bg-emerald-50"
                                            : "border-slate-300 hover:border-indigo-400 bg-gray-50"
                                        }`}
                                >
                                    {(form.documentName)
                                        ? (
                                            <>
                                                <CheckCircle2 className="w-5 h-5 text-emerald-500 mb-1" />
                                                <span className="font-semibold">Image attached</span>
                                                <span className="text-xs text-gray-500">Image file only (.jpg, .png, .webp, etc.)</span>
                                                <input
                                                    type="file"
                                                    accept={IMAGE_MIME_TYPES.join(",")}
                                                    className="hidden"
                                                    ref={inputFileRef}
                                                    onChange={handleFileChange}
                                                />
                                                <span className="mt-2 text-xs bg-emerald-100 px-2 py-1 rounded text-emerald-700 flex items-center gap-2">
                                                    <ImageIcon className="w-4 h-4" />
                                                    {form.documentName}
                                                </span>
                                                {/* Show preview of image */}
                                                {form.document && !editMode && (
                                                    <img
                                                        className="mt-2 rounded shadow max-h-24 object-contain border"
                                                        src={URL.createObjectURL(form.document)}
                                                        alt={form.documentName}
                                                    />
                                                )}
                                            </>
                                        )
                                        : (editMode && form.existingDocumentUrl && !form.document) ? (
                                            <>
                                                <CheckCircle2 className="w-10 h-10 text-emerald-500 mb-2" />
                                                <span className="font-semibold text-center">
                                                    Existing Attachment
                                                </span>
                                                <span className="text-xs text-gray-500">Image file only (.jpg, .png, .webp, etc.)</span>
                                                <input
                                                    type="file"
                                                    accept={IMAGE_MIME_TYPES.join(",")}
                                                    className="hidden"
                                                    ref={inputFileRef}
                                                    onChange={handleFileChange}
                                                />
                                                <span className="mt-2 text-xs bg-indigo-100 px-2 py-1 rounded text-indigo-700 flex items-center gap-2">
                                                    <ImageIcon className="w-4 h-4" />
                                                    <a
                                                        href={form.existingDocumentUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="underline hover:text-indigo-900"
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        {form.existingDocumentName}
                                                    </a>
                                                </span>
                                                {/* (REMOVED: Preview of current image for edit mode) */}
                                                <span className="mt-2 text-xs text-gray-500">
                                                    Click to upload a new image to replace.
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="w-10 h-10 text-indigo-400 mb-2" />
                                                <span className="font-semibold">
                                                    Click to Upload Attachment
                                                </span>
                                                <span className="text-xs text-gray-500">Image file only (.jpg, .png, .webp, etc.)</span>
                                                <input
                                                    type="file"
                                                    accept={IMAGE_MIME_TYPES.join(",")}
                                                    className="hidden"
                                                    ref={inputFileRef}
                                                    onChange={handleFileChange}
                                                />
                                            </>
                                        )
                                    }
                                </div>
                                {editMode && form.existingDocumentUrl && !form.documentName && !form.document && (
                                    <div className="text-xs text-gray-500 mt-2">
                                        Leave as is to keep the existing image.
                                    </div>
                                )}
                                {editMode && form.documentName && (
                                    <div className="flex justify-end mt-1">
                                        <button
                                            type="button"
                                            onClick={e => {
                                                setForm(f => ({
                                                    ...f,
                                                    document: null,
                                                    documentName: '',
                                                    existingDocumentUrl: f.existingDocumentUrl,
                                                    existingDocumentName: f.existingDocumentName
                                                }));
                                                e.stopPropagation();
                                            }}
                                            className="text-xs text-red-600 underline hover:text-red-800"
                                        >
                                            Cancel new image
                                        </button>
                                    </div>
                                )}
                                {editMode && fetchingEditDoc && (
                                    <div className="text-xs text-indigo-500 mt-2 flex items-center gap-2 animate-pulse">
                                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                        </svg>
                                        Fetching attachment for editing...
                                    </div>
                                )}
                            </div>

                            {submitError && <p className="text-red-600">{submitError}</p>}
                            <div className="text-right mb-2">
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="bg-[#3d0707] text-white px-4 py-2 rounded-lg font-semibold shadow hover:bg-[#551010] transition disabled:opacity-70"
                                >
                                    {submitting
                                        ? (addMode ? "Adding..." : "Updating...")
                                        : (addMode ? "Add News" : "Update News")}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="">
                {loading ? (
                    <div className="text-center text-gray-800 py-10">
                        <svg className="animate-spin h-9 w-9 mx-auto text-indigo-500 mb-3" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                        </svg>
                        Loading news...
                    </div>
                ) : fetchError ? (
                    <div className="text-red-500 text-center py-4">{fetchError}</div>
                ) : (
                    <div className='bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden'>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr className="bg-gray-100 uppercase">
                                        <th className="py-2 px-4 text-left text-sm font-bold text-gray-500 uppercase tracking-wider">ID</th>
                                        <th className="py-2 px-4 text-left text-sm font-bold text-gray-500 uppercase tracking-wider">Title</th>
                                        <th className="py-2 px-4 text-left text-sm font-bold text-gray-500 uppercase tracking-wider">Message</th>
                                        <th className="py-2 px-4 text-left text-sm font-bold text-gray-500 uppercase tracking-wider">Attachment</th>
                                        <th className="py-2 px-4 text-left text-sm font-bold text-gray-500 uppercase tracking-wider text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {news.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="py-10 text-center">
                                                No news found.
                                            </td>
                                        </tr>
                                    ) : (
                                        news.map(n => (
                                            <tr key={n.news_id || n.id} className="hover:bg-slate-50 border-t">
                                                <td className="py-2 px-4 whitespace-nowrap">
                                                    <span className="font-mono text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                                        {truncate(String(n.news_id || n.id), 8)}
                                                    </span>
                                                </td>
                                                <td className="py-2 px-4 font-medium">{truncate(n.title, 30)}</td>
                                                <td className="py-2 px-4 font-medium" style={{ whiteSpace: "pre-line" }}>{truncate(n.message, 30)}</td>
                                                <td className="py-2 px-4 text-center">
                                                    {n.document
                                                        ? (
                                                            <a
                                                                href={n.document}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="px-4 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg text-sm font-bold hover:bg-indigo-100 transition shadow-sm text-decoration-none"
                                                            >View</a>
                                                        )
                                                        : <span className="text-gray-400">None</span>
                                                    }
                                                </td>
                                                <td className="py-2 px-4 text-center">
                                                    {/* Actions: Edit and Delete */}
                                                    <div className="flex flex-row justify-center items-center gap-2">
                                                        <button
                                                            onClick={() => openEditModal(n)}
                                                            className="p-2 text-green-600 hover:bg-indigo-50 rounded-lg transition"
                                                            title="Edit"
                                                            type="button"
                                                        >
                                                            <Pencil className="w-4 h-4 mr-1" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(n.news_id || n.id)}
                                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                                                            title="Delete"
                                                            type="button"
                                                        >
                                                            <Trash2 className="w-4 h-4 mr-1" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NewResource;